import {useMe} from '@/features/authentication/queries/useMe.ts'
import {useInfiniteQuery} from '@tanstack/react-query'
import {getChatChannels, GetChatChannelsParams} from '../services/chat.http'
import {QUERY_KEYS} from './keys'
import {generateChatChannelName} from '@/utilities/helpers'
import {Channel} from '../chat.model'
import {USER_ROLE_AGENT, USER_ROLE_REPRESENTATIVE} from '@/utilities/constants/user'

type ChannelsPage = {
    normalizedChannels: Record<string, Channel>
    channelsList: Channel[]
    nextPage?: number
    total: number
}

export const useGetChannelsListV3 = ({
    channelStatus,
    representedCreatorId,
    limit = 10,
    search
}: GetChatChannelsParams) => {
    const {data: user, loggedIn} = useMe()
    return useInfiniteQuery<ChannelsPage>({
        queryKey: [QUERY_KEYS.fetchChannels, representedCreatorId, channelStatus, search],
        initialPageParam: 1,
        queryFn: async ({pageParam = 1}) => {
            if (!user) {
                return {
                    normalizedChannels: {},
                    channelsList: [],
                    nextPage: undefined,
                    total: 0
                }
            }

            const page = typeof pageParam === 'number' ? pageParam : 1
            const response = await getChatChannels({channelStatus, representedCreatorId, page, limit, search})
            const normalized: Record<string, Channel> = {}

            response.data.forEach(ch => {
                const isAgentOrRep = user.type === USER_ROLE_AGENT || user.type === USER_ROLE_REPRESENTATIVE
                const pk = isAgentOrRep
                    ? generateChatChannelName(ch.rookieId, user.type, Number(ch.leaderId))
                    : generateChatChannelName(user.id, user.type, Number(ch.user.id))
                normalized[pk] = {
                    ...ch,
                    pk
                }
            })

            const totalPages = Math.ceil(response.meta?.total / limit)
            const nextPage = page < totalPages ? page + 1 : undefined

            return {
                normalizedChannels: normalized,
                channelsList: Object.values(normalized),
                nextPage,
                total: response.meta.total || 0
            }
        },
        getNextPageParam: (lastPage: {nextPage?: number}) => lastPage?.nextPage ?? undefined,
        enabled: !!user && !!loggedIn && !!representedCreatorId
    })
}
